import {Timber} from '@itero/timber';
import { store } from 'src/store/rootStore';
import { LogLevel, EventType, AppenderType } from '@itero/timber/enums';
import settingsService from 'src/services/settings/settings.service';

class BiLoggingService {
    private timber: Timber;
    private sessionId: string;
    private readonly componentName = 'exocad-3d-viewer';
    private readonly sessionType = 'web';

    async sendSessionStart(externalSessionId: string, sessionStartData: SessionStartData) {
        try {
            const timber = await this.getTimber();
            const data  = {
                externalSessionId:  externalSessionId,
                tags: sessionStartData
            };
            const message = this.buildMessage(BiEventType.SessionStart, data);
            timber.biLog(LogLevel.All, {}, { extendedParameters: message })
        }
        catch (ex) {
            /* istanbul ignore next */
            console.log('[BI]Send SessionStartEvent error: ' + ex)
        }
    }

    async sendSessionTags(tags: any) {
        try {
            const timber = await this.getTimber();
            const data = {
                tags: tags
            };
            const message = this.buildMessage(BiEventType.SessionTags, data);
            timber.biLog(LogLevel.All, {}, { extendedParameters: message });
        }
        catch (ex) {
            /* istanbul ignore next */
            console.log('[BI]Send SessionTagsEvent error: ' + ex)
        }
    }

    async sendStageReport(stageReportData: StageReportData) {
        try {
            const timber = await this.getTimber();
            const message = this.buildMessage(BiEventType.StageReport, stageReportData);
            timber.biLog(LogLevel.All, {}, { extendedParameters: message })
        }
        catch (ex) {
            /* istanbul ignore next */
            console.log('[BI]Send StageReportEvent error: ' + ex)
        }
    }

    async sendUserAction(userActionData: UserActionData) {
        try {
            const timber = await this.getTimber();
            const message = this.buildMessage(BiEventType.UserAction, userActionData);
            timber.biLog(LogLevel.All, {}, { extendedParameters: message })
        }
        catch (ex) {
            /* istanbul ignore next */
            console.log('[BI]Send UserActionEvent error: ' + ex)
        }
    }

    async sendErrorReport(errorReportData: ErrorReportData) {
        try {
            const timber = await this.getTimber();
            const message = this.buildMessage(BiEventType.ErrorReport, errorReportData);
            timber.biLog(LogLevel.All, {}, { extendedParameters: message })
        }
        catch (ex) {
            /* istanbul ignore next */
            console.log('[BI]Send ErrorReportEvent error: ' + ex)
        }
    }

    private async getTimber() {
        if(!this.timber) {
            const {sessionId, companyId, doctorId} = store.getState().session;
            this.sessionId = sessionId;

            const {biLoggingUrl} = await settingsService.getSettings();
            this.timber = new Timber({
                appId: this.componentName,
                appenderTypes: [AppenderType.BIRemote],
                companyId,
                userId: doctorId,
                minLogLevel: LogLevel.All,
                requiredFields: [],
                url: biLoggingUrl,
                eventType: EventType.BusinessReport,
            });
        }
        return this.timber;
    }

    private buildMessage(type: BiEventType, data: any) {
        const message = {
            sessionId: this.sessionId,
            component: this.componentName,
            sessionType: this.sessionType,
            type: type,
            additionalFields: data,
        };
        return message;
    };

}

export default new BiLoggingService();

export enum BiEventType {
    SessionStart = 'SessionStartType',
    SessionTags = 'SessionTagsType',
    StageReport = 'StageReportType',
    UserAction = 'UserActionType',
    ErrorReport = 'ErrorReportType'
}

export enum ActionType {
    Input ='Input',
    Click = 'Click'
}

export enum ObjectType {
    Input ='Input',
    Button = 'Button'
}

export enum SeverityType {
    Error ='Error',
    Warning = 'warning'
}

export interface SessionStartData {
    orderId: string,
    csmMessageId: string,
    userId: string,
    companyId: string
}

export interface UserActionData {
    objectName: string,
    action: ActionType,
    objectType: ObjectType,
    locationParentObject: string
    selectedValue: string;
    automationId: string;
}

export interface StageReportData {
    object: string,
    stage: string
}

export interface ErrorReportData {
    object: string,
    code: string,
    description: string,
    severity: SeverityType
}

